import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { uncertaintyRate, uncertaintyColor } from "../utils/yield-error";

export function UncertaintyImprovementPreview({
  currentSampleCount,
}: {
  currentSampleCount: number;
}) {
  const { t } = useTranslation();

  const currentUncertainty = uncertaintyRate(currentSampleCount) * 100;
  const newUncertainty = uncertaintyRate(currentSampleCount + 1) * 100;
  const currentBarWidth = Math.min((currentUncertainty / 15) * 100, 100);
  const newBarWidth = Math.min((newUncertainty / 15) * 100, 100);
  const currentBarColor = uncertaintyColor(currentUncertainty);
  const newBarColor = uncertaintyColor(newUncertainty);
  const currentLabel = currentUncertainty.toFixed(1);
  const newLabel = newUncertainty.toFixed(1);

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
        borderRadius: 2,
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.success.main, 0.3),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} mb={1.5}>
        <InfoOutlined sx={{ color: "success.main", fontSize: 18 }} />
        <Typography variant="body2" color="success.dark">
          {t("text.calibrate.entry_range", {
            from: currentLabel,
            to: newLabel,
          })}
        </Typography>
      </Stack>
      <Box
        sx={{
          position: "relative",
          height: 10,
          borderRadius: 5,
          bgcolor: "action.disabledBackground",
          display: "flex",
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -2,
            left: "50%",
            transform: "translateX(-50%)",
            width: 1.5,
            height: 14,
            bgcolor: "action.active",
            borderRadius: 1,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: `${(100 - currentBarWidth) / 2}%`,
            width: `${currentBarWidth}%`,
            height: "100%",
            borderRadius: 5,
            bgcolor: currentBarColor,
            opacity: 0.35,
          }}
        />
        <Box
          sx={{
            position: "relative",
            width: `${newBarWidth}%`,
            borderRadius: 5,
            bgcolor: newBarColor,
          }}
        >
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              position: "absolute",
              left: 0,
              top: "100%",
              mt: 0.25,
              transform: "translateX(-50%)",
              color: newBarColor,
              whiteSpace: "nowrap",
              lineHeight: 1,
              fontSize: "0.65rem",
            }}
          >
            {`-${newLabel}%`}
          </Typography>
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{
              position: "absolute",
              right: 0,
              top: "100%",
              mt: 0.25,
              transform: "translateX(50%)",
              color: newBarColor,
              whiteSpace: "nowrap",
              lineHeight: 1,
              fontSize: "0.65rem",
            }}
          >
            {`+${newLabel}%`}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: "5px",
            top: "100%",
            mt: 0.25,
            transform: "translateX(-50%)",
            color: "text.disabled",
            whiteSpace: "nowrap",
            lineHeight: 1,
            fontSize: "0.6rem",
          }}
        >
          {`-${20}%`}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            right: "5px",
            top: "100%",
            mt: 0.25,
            transform: "translateX(50%)",
            color: "text.disabled",
            whiteSpace: "nowrap",
            lineHeight: 1,
            fontSize: "0.6rem",
          }}
        >
          {`+${20}%`}
        </Typography>
      </Box>
      <Box sx={{ height: 16 }} />
    </Box>
  );
}
